import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15_000; // check every 15s while a staff/recruiter tab is open

// Routes where an authenticated session is expected. The poller stays quiet
// everywhere else (marketing, login, signup, forgot-password, etc.) so
// visitors with a lingering session cookie don't see staff/admin toasts on
// the public site.
const PORTAL_PREFIXES = [
  '/admin-dashboard',
  '/staff-dashboard',
  '/recruiter-dashboard',
  '/student-portal',
  '/create-student',
];

const isPortalPath = (pathname) =>
  PORTAL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Silent poller that fetches un-read in-app notifications from the backend
 * and pops them as sonner toasts. Mounts once at the app root so it survives
 * route changes. Only pings while the user is on an authenticated portal
 * route — on 401 we skip until the next tick.
 */
export default function NotificationPoller() {
  const seenIds = useRef(new Set());
  const location = useLocation();
  const active = isPortalPath(location.pathname);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/notifications`, { credentials: 'include' });
        if (!res.ok) return;
        const items = await res.json();
        if (cancelled || !Array.isArray(items)) return;
        for (const n of items) {
          if (seenIds.current.has(n.id)) continue;
          seenIds.current.add(n.id);
          toast(n.title || 'Notification', {
            description: n.body,
            duration: 8000,
            action: {
              label: 'Dismiss',
              onClick: () => {
                fetch(`${API}/api/notifications/${n.id}/dismiss`, { method: 'POST', credentials: 'include' });
              },
            },
          });
        }
      } catch (_) {/* offline / session expired — silently skip */}
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [active]);

  return null;
}
